import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Like2Fit" },
      {
        name: "description",
        content:
          "How Ratecard Technologies Limited collects, uses, and protects your personal information when you use Like2Fit.",
      },
      { property: "og:title", content: "Privacy Policy — Like2Fit" },
      {
        property: "og:description",
        content:
          "How Ratecard Technologies Limited collects, uses, and protects your personal information when you use Like2Fit.",
      },
      { property: "og:url", content: "https://like2fit.com/privacy" },
    ],
    links: [
      { rel: "canonical", href: "https://like2fit.com/privacy" },
    ],
  }),
  component: PrivacyPage,
});

const SECTIONS = [
  "WHAT INFORMATION DO WE COLLECT?",
  "HOW DO WE PROCESS YOUR INFORMATION?",
  "WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?",
  "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?",
  "DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?",
  "DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?",
  "HOW DO WE HANDLE YOUR SOCIAL LOGINS?",
  "HOW LONG DO WE KEEP YOUR INFORMATION?",
  "HOW DO WE KEEP YOUR INFORMATION SAFE?",
  "DO WE COLLECT INFORMATION FROM MINORS?",
  "WHAT ARE YOUR PRIVACY RIGHTS?",
  "CONTROLS FOR DO-NOT-TRACK FEATURES",
  "DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?",
  "DO WE MAKE UPDATES TO THIS NOTICE?",
  "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?",
  "HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?",
];

const EMAIL = "support@like2fit.com";

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link
          to="/"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Back
        </Link>

        <h1 className="text-3xl font-semibold mt-6">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Last updated July 11, 2026
        </p>

        <div className="mt-8 space-y-5 text-[15px] leading-relaxed">
          <p>
            This Privacy Notice for Ratecard Technologies Limited ('we', 'us',
            or 'our') describes how and why we might access, collect, store,
            use, and/or share ('process') your personal information when you
            use our services ('Services'), including when you:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Visit our website at{" "}
              <a
                href="http://www.like2fit.com"
                className="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                http://www.like2fit.com
              </a>{" "}
              or any website of ours that links to this Privacy Notice.
            </li>
            <li>
              Use Like2Fit. Like2Fit is an AI-powered fashion discovery and
              styling web application. Users can upload outfit screenshots
              from social media to identify every clothing item and find
              similar products across hundreds of retailers at personalised
              budget tiers. Users can also upload photos of clothing they
              already own to receive AI-generated outfit pairing suggestions
              for specific occasions. The app generates AI fashion editorial
              images showing complete outfits on a personalised model based
              on user-provided physical characteristics including height,
              body shape, skin tone and hair type.
            </li>
            <li>Engage with us in other related ways, including any marketing or events.</li>
          </ul>
          <p>
            <strong>Questions or concerns?</strong> Reading this Privacy
            Notice will help you understand your privacy rights and choices.
            We are responsible for making decisions about how your personal
            information is processed. If you do not agree with our policies
            and practices, please do not use our Services. If you still have
            any questions or concerns, please contact us at{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
            .
          </p>
        </div>

        <h2 className="text-xl font-semibold mt-12">Summary of Key Points</h2>
        <p className="text-sm text-muted-foreground mt-1">
          This summary provides key points from our Privacy Notice. You can
          find more detail in the full sections below.
        </p>
        <div className="mt-4 space-y-4 text-[15px] leading-relaxed">
          <p>
            <strong>What personal information do we process?</strong> When you
            visit, use, or navigate our Services, we may process personal
            information depending on how you interact with us and the
            Services, the choices you make, and the products and features you
            use.
          </p>
          <p>
            <strong>Do we process any sensitive personal information?</strong>{" "}
            Some of the information may be considered 'special' or
            'sensitive' in certain jurisdictions. We may process sensitive
            personal information when necessary with your consent or as
            otherwise permitted by applicable law.
          </p>
          <p>
            <strong>Do we collect any information from third parties?</strong>{" "}
            We do not collect any information from third parties.
          </p>
          <p>
            <strong>How do we process your information?</strong> We process
            your information to provide, improve, and administer our
            Services, communicate with you, for security and fraud
            prevention, and to comply with law. We may also process your
            information for other purposes with your consent.
          </p>
          <p>
            <strong>
              In what situations and with which parties do we share personal
              information?
            </strong>{" "}
            We may share information in specific situations and with specific
            third parties.
          </p>
          <p>
            <strong>How do we keep your information safe?</strong> We have
            adequate organisational and technical processes and procedures in
            place to protect your personal information. However, no
            electronic transmission over the internet or information storage
            technology can be guaranteed to be 100% secure.
          </p>
          <p>
            <strong>What are your rights?</strong> Depending on where you are
            located geographically, applicable privacy law may give you
            certain rights regarding your personal information.
          </p>
          <p>
            <strong>How do you exercise your rights?</strong> The easiest way
            to exercise your rights is by submitting a data subject access
            request, or by contacting us. We will consider and act upon any
            request in accordance with applicable data protection laws.
          </p>
        </div>

        <h2 className="text-xl font-semibold mt-12">Table of Contents</h2>
        <ol className="mt-3 list-decimal pl-6 space-y-1.5 text-[15px]">
          {SECTIONS.map((s, i) => (
            <li key={s}>
              <a href={`#section-${i + 1}`} className="underline">
                {s}
              </a>
            </li>
          ))}
        </ol>

        <section id="section-1" className="mt-12">
          <h2 className="text-xl font-semibold">1. What information do we collect?</h2>
          <h3 className="font-semibold mt-4">Personal information you disclose to us</h3>
          <p className="italic text-sm text-muted-foreground mt-1">
            In Short: We collect personal information that you provide to us.
          </p>
          <p className="mt-3">
            We collect personal information that you voluntarily provide to
            us when you register on the Services, express an interest in
            obtaining information about us or our products and Services,
            participate in activities on the Services, or otherwise contact
            us.
          </p>
          <p className="mt-3">
            <strong>Personal Information Provided by You.</strong> The
            personal information we collect may include:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>names</li>
            <li>email addresses</li>
            <li>passwords</li>
            <li>usernames</li>
            <li>contact or authentication data</li>
          </ul>
          <p className="mt-3">
            <strong>Sensitive Information.</strong> When necessary, with your
            consent or as otherwise permitted by applicable law, we process
            the following categories of sensitive information:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>
              biometric data — specifically, an optional selfie photograph
              you upload to personalise AI-generated lookbook images so the
              model resembles you
            </li>
            <li>
              physical characteristics you enter to generate lookbook models
              (height, body shape, skin tone, hair type, and optional age)
            </li>
          </ul>
          <p className="mt-3">
            Selfies and personalisation characteristics are processed only
            with your explicit in-app consent (the "Personalisation" toggle
            in the Account tab). You can turn personalisation off, replace
            your selfie, or delete it at any time from the same screen. See
            section 8 for retention and section 16 for deletion.
          </p>
          <p className="mt-3">
            <strong>Social Media Login Data.</strong> We may provide you with
            the option to register with us using your existing social media
            account details. If you choose to register in this way, we will
            collect certain profile information from the social media
            provider, as described in section 7.
          </p>
          <p className="mt-3">
            All personal information that you provide to us must be true,
            complete, and accurate, and you must notify us of any changes.
          </p>

          <h3 className="font-semibold mt-6">Information automatically collected</h3>
          <p className="italic text-sm text-muted-foreground mt-1">
            In Short: Some information — such as your IP address and/or
            browser and device characteristics — is collected automatically
            when you visit our Services.
          </p>
          <p className="mt-3">
            We automatically collect certain information when you visit, use,
            or navigate the Services. This information does not reveal your
            specific identity but may include device and usage information,
            such as your IP address, browser and device characteristics,
            operating system, language preferences, referring URLs, device
            name, country, location, information about how and when you use
            our Services, and other technical information.
          </p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li>
              <strong>Log and Usage Data.</strong> Service-related,
              diagnostic, usage, and performance information our servers
              automatically collect when you access or use our Services.
            </li>
            <li>
              <strong>Device Data.</strong> Information about your computer,
              phone, tablet, or other device used to access the Services.
            </li>
            <li>
              <strong>Location Data.</strong> Information about your device's
              location, which can be either precise or imprecise.
            </li>
          </ul>
          <p className="mt-3">
            <strong>Google API.</strong> Our use of information received from
            Google APIs will adhere to the Google API Services User Data
            Policy, including the Limited Use requirements.
          </p>

          <h3 className="font-semibold mt-6">1.a Apple Sign-In data</h3>
          <p className="mt-2">
            When you sign in with Apple on iOS, we receive an Apple-issued
            identity token containing a stable user identifier
            (<code>sub</code>) and an email address (either your real Apple
            ID email or, if you choose Apple's <strong>Hide My
            Email</strong> option, a relay address of the form{" "}
            <code>xxx@privaterelay.appleid.com</code>). On your first
            sign-in only, Apple also returns your full name if you consent
            in the system dialog. We record your acceptance of the Apple
            consent dialog as part of the sign-in event.
          </p>

          <h3 className="font-semibold mt-6">1.b Google Sign-In data</h3>
          <p className="mt-2">
            When you sign in with Google, we receive a Google-issued
            identity token containing your Google account email, your name,
            and, if you grant the relevant scope, your profile picture URL.
          </p>

          <h3 className="font-semibold mt-6">1.c Native plugin data flow</h3>
          <p className="mt-2">
            On native iOS and Android, the identity token returned by Apple
            or Google is passed through the Capgo social-login plugin to
            our authentication backend (Supabase Auth, hosted on Lovable
            Cloud) solely to verify the token and create your Like2Fit
            session. No identity-token data is shared with any third party
            other than the issuing provider (Apple or Google) and our
            authentication backend.
          </p>
        </section>

        <section id="section-2" className="mt-12">
          <h2 className="text-xl font-semibold">2. How do we process your information?</h2>
          <p className="italic text-sm text-muted-foreground mt-1">
            In Short: We process your information to provide, improve, and
            administer our Services, communicate with you, for security and
            fraud prevention, and to comply with law.
          </p>
          <p className="mt-3">
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including to
            facilitate account creation and authentication and otherwise
            manage user accounts. We may process your information so you can
            create and log in to your account, as well as keep your account
            in working order.
          </p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li>
              <strong>Account creation and authentication.</strong> We
              process the data described in section 1 to create and
              authenticate your account via Apple Sign-In, Google Sign-In,
              or email and password.
            </li>
            <li>
              <strong>Email confirmation.</strong> When you register with an
              email and password, we send you a verification email; your
              account is activated only after you confirm your email
              address via the link in that email.
            </li>
            <li>
              <strong>Apple Hide My Email.</strong> When you choose Apple's
              Hide My Email option, all email we send (including
              verification and account-related messages) is delivered to
              the relay address Apple provides us. We cannot identify or
              contact your real email address through that relay.
            </li>
          </ul>
        </section>

        {SECTIONS.slice(2).map((title, idx) => {
          const n = idx + 3;

          if (n === 3) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  3. What legal bases do we rely on to process your personal information?
                </h2>
                <p className="mt-3">
                  We rely on the following legal bases under UK GDPR and the Data
                  Protection Act 2018 to process your personal information:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    <strong>Consent</strong> — for optional processing such as your
                    selfie photograph and physical characteristics used for
                    personalisation, marketing emails you opt into, and non-essential
                    cookies. You can withdraw consent at any time from the Account
                    tab.
                  </li>
                  <li>
                    <strong>Contract performance</strong> — for processing needed to
                    create your account, deliver outfit analyses, process
                    subscription payments, and provide the core Services you signed
                    up for.
                  </li>
                  <li>
                    <strong>Legitimate interests</strong> — for security, fraud
                    prevention, aggregate analytics to improve the Services, and
                    communicating essential service updates. You can object to
                    processing based on legitimate interests by contacting us.
                  </li>
                  <li>
                    <strong>Legal obligation</strong> — where required by applicable
                    law, including retention of financial records for tax purposes
                    and responding to lawful requests from regulators.
                  </li>
                  <li>
                    <strong>Vital interests</strong> — in rare cases to protect the
                    vital interests of you or another person.
                  </li>
                </ul>
              </section>
            );
          }

          if (n === 4) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  4. When and with whom do we share your personal information?
                </h2>
                <p className="mt-3">
                  We share personal information with a limited set of
                  third-party processors that operate the Services on our
                  behalf:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>
                    <strong>OpenAI.</strong> We use OpenAI's{" "}
                    <code>gpt-image-2</code> API to generate outfit
                    visualisations and analyse uploaded garment imagery.
                    Images you upload are transmitted to OpenAI for
                    processing and are subject to OpenAI's data-retention
                    policy. If you have enabled personalisation, your selfie
                    photograph is also transmitted to OpenAI as a reference
                    image on every lookbook render so the generated model
                    resembles you. OpenAI retains API inputs for up to 30
                    days for abuse monitoring and does not use them to train
                    its models.
                  </li>
                  <li>
                    <strong>SerpApi.</strong> We use SerpApi to search for
                    matching products across retailers. Product imagery and
                    descriptions are queried, but no user PII is shared
                    with SerpApi.
                  </li>
                  <li>
                    <strong>Skimlinks.</strong> Affiliate clicks originating
                    in our app are routed via Skimlinks so we can receive
                    commission on qualifying purchases.
                  </li>
                  <li>
                    <strong>Resend.</strong> We use Resend to send
                    transactional email (verification, account-deletion
                    confirmation, daily outfit emails you have opted into).
                  </li>
                  <li>
                    <strong>Apple and Google.</strong> Sign-in providers
                    receive the data required to authenticate you, per
                    their own privacy policies.
                  </li>
                </ul>
              </section>
            );
          }

          if (n === 5) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  5. Do we use cookies and other tracking technologies?
                </h2>
                <p className="mt-3">
                  We use minimal cookies and local storage to operate the
                  Services:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    <strong>Essential storage</strong> — we use browser
                    localStorage on the web and secure keychain on native mobile
                    to store your authentication session, guest identifier, and
                    in-app preferences (personalisation toggle, offline shopping
                    location, plan selection). Without these, core Services will
                    not function.
                  </li>
                  <li>
                    <strong>Analytics</strong> — we use PostHog and Supabase
                    Analytics to track anonymised product usage patterns (screens
                    visited, features used, conversion events). Analytics cookies
                    do not track you across other websites.
                  </li>
                  <li>
                    <strong>Marketing</strong> — we do not use marketing or
                    advertising cookies. We do not sell your data to advertisers.
                  </li>
                  <li>
                    <strong>Third-party embeds</strong> — some parts of the web
                    Services may load resources from Google (Sign-In), Apple
                    (Sign-In), and Skimlinks (affiliate redirection). These
                    providers may set their own cookies subject to their privacy
                    policies.
                  </li>
                </ul>
                <p className="mt-3">
                  You can clear browser storage at any time via your browser
                  settings. Signing out clears your session storage automatically.
                  Do Not Track browser signals are respected where technically
                  feasible.
                </p>
              </section>
            );
          }

          if (n === 6) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  6. Do we offer artificial intelligence-based products?
                </h2>
                <p className="mt-3">
                  Yes. Like2Fit uses artificial intelligence to power core
                  features:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    <strong>Outfit analysis</strong> — when you paste an outfit
                    link or upload a photo, we use OpenAI's <code>gpt-image-2</code>{" "}
                    API and custom prompts to identify garments, evaluate fit and
                    style, and generate personalised recommendations. Your input
                    images are sent to OpenAI for processing.
                  </li>
                  <li>
                    <strong>Lookbook generation</strong> — if you enable
                    personalisation, we generate AI images showing outfits on a
                    model matching your physical characteristics. Your selfie (if
                    uploaded) is transmitted to OpenAI as a reference image on
                    every render.
                  </li>
                  <li>
                    <strong>Style reasoning</strong> — we use large language
                    models to generate written reasoning and styling suggestions
                    tailored to your inputs.
                  </li>
                </ul>
                <p className="mt-3">
                  AI outputs are recommendations, not guarantees. AI may
                  occasionally produce inaccurate or unexpected results. We do
                  not use AI to make automated decisions that produce legal or
                  similarly significant effects about you (such as credit,
                  employment, or insurance decisions).
                </p>
                <p className="mt-3">
                  You have the right to opt out of AI-based personalisation at
                  any time via the Personalisation toggle in the Account tab.
                  Core outfit analysis cannot be disabled without ceasing use of
                  the Services, as it is the primary feature.
                </p>
              </section>
            );
          }

          if (n === 7) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  7. How do we handle your social logins?
                </h2>
                <p className="mt-3">
                  We offer Sign in with Apple and Sign in with Google as
                  authentication options.
                </p>
                <p className="mt-3">
                  When you use these providers, we receive only the data
                  necessary to identify and authenticate your account: a stable
                  user identifier issued by the provider, your email address (or
                  Apple's relay address if you use Hide My Email), and your name
                  on first sign-in only. Where you consent to it, we may also
                  receive your profile picture URL from Google.
                </p>
                <p className="mt-3">
                  We do not receive access to your other Apple or Google account
                  data (calendar, contacts, photos, files, etc.). We do not post
                  anything back to your Apple or Google account.
                </p>
                <p className="mt-3">
                  Provider tokens are used solely to verify your identity with
                  our authentication backend (Supabase Auth) and create your
                  Like2Fit session. Tokens are not shared with any other third
                  party.
                </p>
                <p className="mt-3">
                  If you sign in with Apple using Hide My Email, we can only
                  reach you through the relay address Apple provides. If Apple
                  deactivates that relay, we lose the ability to email you.
                </p>
                <p className="mt-3">
                  You can disconnect Like2Fit from your Apple or Google account
                  at any time from your provider's account settings.
                  Disconnecting will prevent future sign-ins but does not
                  automatically delete your Like2Fit account — use the in-app
                  Delete Account flow for that.
                </p>
              </section>
            );
          }

          if (n === 8) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  8. How long do we keep your information?
                </h2>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    <strong>Wardrobe items, outfits, and lookbooks</strong>{" "}
                    are retained until you delete your account.
                  </li>
                  <li>
                    <strong>Generated images</strong> (gpt-image-2 outputs)
                    are retained until you delete your account.
                  </li>
                  <li>
                    <strong>Selfie photograph</strong> (if you have enabled
                    personalisation) is stored in a private bucket and
                    retained until you delete it from the Account tab, turn
                    personalisation off, or delete your account. Lookbook
                    images already generated from a selfie persist until you
                    delete those outfits or your account.
                  </li>
                  <li>
                    <strong>Analytics events</strong> are retained
                    indefinitely. When you delete your account, your user
                    identifier is removed from analytics events so they can
                    no longer be linked back to you; the anonymised events
                    remain for aggregate product metrics.
                  </li>
                  <li>
                    <strong>Account-deletion audit log</strong> entries are
                    retained for 90 days for compliance purposes and then
                    erased.
                  </li>
                  <li>
                    <strong>Anonymous analyses</strong> created without an
                    account are automatically deleted after 7 days to minimise
                    unnecessary data retention.
                  </li>
                </ul>
              </section>
            );
          }

          if (n === 9) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  9. How do we keep your information safe?
                </h2>
                <p className="mt-3">
                  We use industry-standard security measures to protect your
                  personal information:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    <strong>Encryption</strong> — all data in transit is
                    encrypted using TLS 1.2 or higher. Data at rest is encrypted
                    using AES-256 in our database (Supabase, hosted on AWS in
                    the EU).
                  </li>
                  <li>
                    <strong>Access controls</strong> — access to personal data
                    is limited to authorised personnel and third-party
                    processors under contract. Row-level security policies
                    enforce that users can only access their own data.
                  </li>
                  <li>
                    <strong>Authentication</strong> — passwords are hashed using
                    bcrypt. Social sign-in tokens are verified server-side.
                    Sessions expire and refresh regularly.
                  </li>
                  <li>
                    <strong>Selfies and personalisation photos</strong> — stored
                    in a private, access-controlled storage bucket. Only you and
                    the AI generation service (OpenAI) can access them, and only
                    during generation.
                  </li>
                  <li>
                    <strong>Payment data</strong> — we do not store payment card
                    details. Subscription payments are processed by Apple
                    through the App Store. We receive only a receipt
                    confirmation via RevenueCat, our subscription management
                    provider.
                  </li>
                </ul>
                <p className="mt-3">
                  Despite our safeguards, no system is 100% secure. If we become
                  aware of a data breach affecting your personal information, we
                  will notify you and applicable regulators as required by law.
                </p>
              </section>
            );
          }

          if (n === 10) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  10. Do we collect information from minors?
                </h2>
                <p className="mt-3">
                  Like2Fit is not intended for users under 13 years of age. We
                  do not knowingly collect personal information from children
                  under 13.
                </p>
                <p className="mt-3">
                  If we discover that a user under 13 has provided personal
                  information, we will delete the account and associated data
                  promptly. If you believe a child has provided personal
                  information without parental consent, please contact us at{" "}
                  <a href={`mailto:${EMAIL}`} className="underline">
                    {EMAIL}
                  </a>
                  .
                </p>
                <p className="mt-3">
                  Users between 13 and 18 should use the Services only with the
                  consent of a parent or guardian. Some features (including
                  subscription purchases) may require age verification or
                  parental approval through Apple's App Store family sharing
                  controls.
                </p>
              </section>
            );
          }

          if (n === 11) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  11. What are your privacy rights?
                </h2>
                <p className="mt-3">
                  Depending on your location, you have the following rights
                  under UK GDPR, EU GDPR, and comparable laws:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    <strong>Right of access</strong> — request a copy of the
                    personal information we hold about you.
                  </li>
                  <li>
                    <strong>Right to rectification</strong> — correct inaccurate
                    or incomplete personal information.
                  </li>
                  <li>
                    <strong>Right to erasure</strong> — delete your personal
                    information (subject to legal retention requirements). You
                    can initiate this through the in-app Delete Account flow.
                  </li>
                  <li>
                    <strong>Right to restrict processing</strong> — limit how we
                    process your personal information.
                  </li>
                  <li>
                    <strong>Right to data portability</strong> — receive your
                    personal information in a structured, machine-readable
                    format.
                  </li>
                  <li>
                    <strong>Right to object</strong> — object to processing
                    based on legitimate interests or direct marketing.
                  </li>
                  <li>
                    <strong>Right to withdraw consent</strong> — where
                    processing is based on consent, withdraw it at any time.
                  </li>
                  <li>
                    <strong>
                      Right not to be subject to automated decision-making
                    </strong>{" "}
                    — we do not make automated decisions that have legal or
                    similarly significant effects about you.
                  </li>
                  <li>
                    <strong>Right to lodge a complaint</strong> — with the UK
                    Information Commissioner's Office (
                    <a
                      href="https://ico.org.uk"
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ico.org.uk
                    </a>
                    ) or your local supervisory authority.
                  </li>
                </ul>
                <p className="mt-3">
                  To exercise any of these rights, use the in-app controls in
                  the Account tab or email{" "}
                  <a href={`mailto:${EMAIL}`} className="underline">
                    {EMAIL}
                  </a>
                  . We will respond within 30 days.
                </p>
              </section>
            );
          }

          if (n === 12) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  12. Controls for do-not-track features
                </h2>
                <p className="mt-3">
                  Most web browsers include a Do Not Track (DNT) setting. We
                  honour DNT signals where technically feasible by disabling
                  non-essential analytics and tracking cookies. However, there
                  is no industry-standard DNT protocol, and some third-party
                  providers may not respect DNT signals.
                </p>
                <p className="mt-3">
                  You can further limit tracking by clearing browser storage,
                  using private browsing mode, or opting out of analytics via
                  the Account tab.
                </p>
              </section>
            );
          }

          if (n === 13) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  13. Do United States residents have specific privacy rights?
                </h2>
                <p className="mt-3">
                  Yes. Residents of California, Colorado, Connecticut, Utah,
                  Virginia, and other US states with comprehensive privacy laws
                  have specific rights regarding their personal information,
                  including:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    Right to know what personal information we collect, use,
                    disclose, and sell.
                  </li>
                  <li>Right to delete personal information we have collected.</li>
                  <li>Right to correct inaccurate personal information.</li>
                  <li>
                    Right to opt out of the sale or sharing of personal
                    information for cross-context behavioural advertising. We do
                    not sell your personal information and do not share it for
                    cross-context behavioural advertising.
                  </li>
                  <li>
                    Right to limit the use of sensitive personal information.
                  </li>
                  <li>
                    Right to non-discrimination for exercising your privacy
                    rights.
                  </li>
                </ul>
                <p className="mt-3">
                  To exercise these rights, use the in-app controls in the
                  Account tab or email{" "}
                  <a href={`mailto:${EMAIL}`} className="underline">
                    {EMAIL}
                  </a>
                  . We will respond within the timeframes required by applicable
                  law.
                </p>
              </section>
            );
          }

          if (n === 14) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  14. Do we make updates to this notice?
                </h2>
                <p className="mt-3">
                  Yes. We may update this Privacy Notice from time to time to
                  reflect changes in our practices, the Services, or applicable
                  law. When we make material changes, we will:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    Update the "Last updated" date at the top of this notice.
                  </li>
                  <li>
                    Notify you via in-app notification or email at least 14 days
                    before material changes take effect.
                  </li>
                  <li>Where required by law, obtain your renewed consent.</li>
                </ul>
                <p className="mt-3">
                  Continued use of the Services after changes take effect
                  constitutes acceptance of the updated Privacy Notice. If you
                  do not agree with the changes, you should delete your account
                  before they take effect.
                </p>
              </section>
            );
          }

          if (n === 15) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  15. How can you contact us about this notice?
                </h2>
                <p className="mt-3">
                  If you have questions or concerns about this Privacy Notice or
                  our privacy practices, contact us at:
                </p>
                <p className="mt-3">
                  Ratecard Technologies Limited
                  <br />
                  Email:{" "}
                  <a href={`mailto:${EMAIL}`} className="underline">
                    {EMAIL}
                  </a>
                </p>
                <p className="mt-3">
                  For UK residents, our data protection representative can be
                  reached at the same email address.
                </p>
                <p className="mt-3">
                  If you are unsatisfied with our response, you have the right
                  to lodge a complaint with the UK Information Commissioner's
                  Office at{" "}
                  <a
                    href="https://ico.org.uk"
                    className="underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ico.org.uk
                  </a>
                  .
                </p>
              </section>
            );
          }

          if (n === 16) {
            return (
              <section key={title} id={`section-${n}`} className="mt-12">
                <h2 className="text-xl font-semibold">
                  16. How can you review, update, or delete the data we
                  collect from you?
                </h2>
                <p className="mt-3">
                  You can delete your Like2Fit account at any time from the{" "}
                  <strong>Account tab → Delete Account</strong> in the app.
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>
                    On deletion, your account is immediately{" "}
                    <strong>soft-deleted</strong> and becomes inaccessible
                    — you can no longer sign in or use the Services.
                  </li>
                  <li>
                    You have a <strong>30-day recovery window</strong>:
                    email{" "}
                    <a href={`mailto:${EMAIL}`} className="underline">
                      {EMAIL}
                    </a>{" "}
                    from your account address within 30 days to restore
                    the account.
                  </li>
                  <li>
                    After 30 days, all of your personal data is{" "}
                    <strong>permanently deleted</strong>: wardrobe items,
                    outfits, lookbooks, generated images, your selfie
                    photograph (if any), profile, preferences, and
                    authentication records (including the Apple/Google
                    identifier we received at sign-in).
                  </li>
                  <li>
                    Analytics events tied to your account are anonymised
                    (user identifier removed) but retained for aggregate
                    metrics, as described in section 8.
                  </li>
                  <li>
                    To remove Like2Fit's access from the provider side as
                    well, revoke the app in your{" "}
                    <strong>Apple ID</strong> settings (Settings → Apple
                    Account → Sign in with Apple) and/or your{" "}
                    <strong>Google account</strong> (myaccount.google.com →
                    Security → Third-party apps).
                  </li>
                </ul>
                <p className="mt-3">
                  For any other request to review or update your data,
                  contact us at{" "}
                  <a href={`mailto:${EMAIL}`} className="underline">
                    {EMAIL}
                  </a>
                  .
                </p>
              </section>
            );
          }

          return null;
        })}


        <section className="mt-16 border-t pt-6 text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-2">
          <span>
            Contact: Ratecard Technologies Limited —{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
          </span>
          <Link to="/terms" className="underline">
            Terms of Service
          </Link>
          <Link to="/account-deletion" className="underline">
            Delete your account
          </Link>
        </section>
      </div>
    </div>
  );
}
